import React, { useEffect, useMemo, useState } from 'react';
import { useUser } from '@/contexts/UserContext';
import { useLocation } from 'react-router-dom';
import { usePurchaseOrders, fetchPurchaseOrderById } from './store';
import { useOpenEntityRecord } from '@/hooks/useOpenEntity';
import { usePurchaseOrderFormOptions } from './usePurchaseOrderFormOptions';
import { SectionLayout } from '@/components/section-layout';
import { PurchaseOrdersTable } from './components/PurchaseOrdersTable';
import { PurchaseOrderForm } from './components/PurchaseOrderForm';
import { fetchRelatedBills, relatedBillsNotice } from '../bills/services/billPurchaseOrdersService';
import { PurchaseOrder, NewPurchaseOrderInput } from './types';
import { Copy, Trash2, Download, Upload } from 'lucide-react';
import { getApiUrl } from '@/lib/api-config';
import { NewItemButton, SearchButton, ThreeDotMenuButton } from '@/styles/StandardButtons';
import { trackButtonClick } from '@/lib/posthog-events';
import { exportEntityCsv } from '@/lib/csv';



export function PurchaseOrdersPage() {
  const { companyId, user } = useUser();
  const location = useLocation();
  const { items, addPurchaseOrder, updatePurchaseOrder, exportRows, setSearchInput, queryKey, tableProps, reload } = usePurchaseOrders();
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<PurchaseOrder | null>(null);
  const { projects, users, vendors, currentAuthor, projectIdToName, vendorIdToName } = usePurchaseOrderFormOptions();
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  // The list is a new query when the project, the search or the sort changes: rows selected in the previous one are gone.
  useEffect(() => {
    setSelectedRows([]);
  }, [queryKey]);

  // a link from another document (Linked Docs) opens this purchase order
  useOpenEntityRecord('purchase_order', fetchPurchaseOrderById, (po) => { setEditing(po); setEditOpen(true); });

  // Check if we should open the form from navigation
  useEffect(() => {
    if (location.state?.openForm) {
      setCreateOpen(true);
      // Clear the state to prevent reopening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);



  async function handleCreate(data: NewPurchaseOrderInput) {
    const created = await addPurchaseOrder(data, currentAuthor);
    return created;
  }

  async function handleEdit(data: NewPurchaseOrderInput) {
    if (editing) {
      const updated = await updatePurchaseOrder(editing.id, data);
      return updated;
    }
    return undefined;
  }

  // Handle copy selected purchase orders
  const handleCopySelected = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one purchase order to copy.');
      return;
    }

    if (!companyId || !user?.id) {
      alert('Please log in again to copy purchase orders');
      return;
    }

    try {
      const selectedPOs = items.filter(po => selectedRows.includes(po.id));
      let successCount = 0;
      let errorCount = 0;

      // Copy each selected purchase order - let API generate po_number automatically
      for (const po of selectedPOs) {
        try {
          const response = await fetch(getApiUrl('api/entities/purchase_order'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: user.id,
              company_id: companyId,
              // Don't include po_number - let API generate it
              project_id: po.projectId,
              vendor_id: po.vendorId || null,
              title: po.title,
              description: po.description || null,
              amount: po.amount,
              status: 'draft', // Always set to draft for copies
              complete_by: po.receivedAt ? new Date(po.receivedAt + 'T00:00:00').toISOString() : null,
            })
          });

          if (!response.ok) {
            const errorData = await response.json();
            console.error('Error copying purchase order:', errorData);
            errorCount++;
            continue;
          }
          successCount++;
        } catch (error) {
          console.error('Error copying purchase order:', error);
          errorCount++;
        }
      }

      // Refresh the list
      window.location.reload();
      setSelectedRows([]);
      if (errorCount > 0) {
        alert(`Copied ${successCount} purchase order(s) successfully. ${errorCount} failed.`);
      } else {
        alert(`Successfully copied ${successCount} purchase order(s).`);
      }
    } catch (error) {
      console.error('Error copying purchase orders:', error);
      alert('Error copying purchase orders. Please try again.');
    }
  };

  // Handle delete selected purchase orders
  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one purchase order to delete.');
      return;
    }

    // A purchase order that has bills CAN be deleted: its bills are kept and only lose the link. The confirmation says so before anything happens.
    let billsNotice = '';
    try {
      const related = await fetchRelatedBills(selectedRows);
      const withBills = items
        .filter((order) => selectedRows.includes(order.id) && (related[order.id] || []).length > 0)
        .map((order) => ({ po_number: order.poNumber, title: order.title, bills: related[order.id] }));
      if (withBills.length > 0) billsNotice = `\n\n${relatedBillsNotice(withBills)}`;
    } catch (error) {
      console.error('Could not check the related bills (the bills are kept either way):', error);
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedRows.length} purchase order(s)? This action cannot be undone.${billsNotice}`)) {
      return;
    }

    if (!companyId || !user?.id) {
      alert('Please log in again to delete purchase orders');
      return;
    }

    try {
      let successCount = 0;
      let errorCount = 0;
      let firstError = '';

      for (const poId of selectedRows) {
        try {
          const response = await fetch(getApiUrl(`api/entities/purchase_order/${poId}`), {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: user.id,
              company_id: companyId
            })
          });

          if (!response.ok) {
            const errorData = await response.json();
            console.error('Error deleting purchase order:', errorData);
            if (!firstError) firstError = errorData?.error || errorData?.message || '';
            errorCount++;
            continue;
          }
          successCount++;
        } catch (error) {
          console.error('Error deleting purchase order:', error);
          errorCount++;
        }
      }

      // Refresh the list
      window.location.reload();
      setSelectedRows([]);
      if (errorCount > 0) {
        alert(`Deleted ${successCount} purchase order(s) successfully. ${errorCount} failed.${firstError ? `\n\n${firstError}` : ''}`);
      } else {
        alert(`Successfully deleted ${successCount} purchase order(s).`);
      }
    } catch (error) {
      console.error('Error deleting purchase orders:', error);
      alert('Error deleting purchase orders. Please try again.');
    }
  };

  // Export to CSV: the rows selected in the table, or -- with nothing selected -- EVERY purchase order of the
  // current project + search, read by the backend (not only the pages loaded in the table).
  const handleExport = () => exportEntityCsv(exportRows, selectedRows, 'purchase-orders', 'purchase orders');

  const menuItems = [
    {
      label: 'Copy',
      icon: <Copy className="w-4 h-4" />,
      onClick: handleCopySelected,
    },
    {
      label: 'Delete',
      icon: <Trash2 className="w-4 h-4" />,
      onClick: handleDeleteSelected,
      destructive: true,
    },
    {
      label: 'Import',
      icon: <Upload className="w-4 h-4" />,
      onClick: () => {},
    },
    {
      label: 'Export',
      icon: <Download className="w-4 h-4" />,
      onClick: handleExport,
    },
  ];

  return (
    <SectionLayout title="Purchase Orders">
      <div className="space-y-6">
        {/* Action Buttons */}
        <div className="flex items-center justify-end">
          <div className="flex items-center gap-2">
            <NewItemButton
              label="New"
              onClick={() => {
                trackButtonClick('new_purchase_order_button', { context: 'cost_management' });
                setCreateOpen(true);
              }}
            />
            <SearchButton
              onSearch={setSearchInput}
              placeholder="Search"
            />
            <ThreeDotMenuButton items={menuItems} />
          </div>
        </div>

        {/* Table */}
        <PurchaseOrdersTable
          tableProps={tableProps}
          onOpen={(po) => { setEditing(po); setEditOpen(true); }}
          projectIdToName={projectIdToName}
          vendorIdToName={vendorIdToName}
          selectedRows={selectedRows}
          onSelectedRowsChange={setSelectedRows}
        />
      </div>

      <PurchaseOrderForm
        purchaseOrder={null}
        projects={projects}
        users={users}
        vendors={vendors}
        onClose={() => setCreateOpen(false)}
        onSave={handleCreate}
        isOpen={createOpen}
      />

      <PurchaseOrderForm
        purchaseOrder={editing}
        projects={projects}
        users={users}
        vendors={vendors}
        onClose={() => { setEditOpen(false); setEditing(null); }}
        onSave={handleEdit}
        onSent={reload}
        isOpen={editOpen}
      />
    </SectionLayout>
  );
} 